import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Heart } from "lucide-react";
import api from "@/lib/api";
import { COUNTRIES } from "@/components/home/data";

const inputCls = "w-full mt-2 bg-transparent border-b border-border py-2.5 outline-none focus:border-accent transition-colors placeholder:text-muted-foreground/50";
const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-muted-foreground">{children}</label>;

export default function GuideInfo() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [form, setForm] = useState({ name: "", whatsapp: "", whatsapp_same: true, email: "", profession: "", place: "" });
  const [dialCode, setDialCode] = useState("+91");
  const [mobileLocal, setMobileLocal] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/guide-reg/validate`, { params: { token } })
      .then(({ data }) => setMeta(data))
      .catch((err) => setInvalid(err?.response?.data?.detail || "This link is not valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const digits = mobileLocal.replace(/\D/g, "");
    const country = COUNTRIES.find((c) => c.code === dialCode);
    if (country?.len && digits.length !== country.len) { setError(`Mobile number should be ${country.len} digits for ${country.name}`); return; }
    if (dialCode !== "+91" && /^[6-9]\d{9}$/.test(digits) && !window.confirm(`You chose ${dialCode} (${country?.name}). This looks like an Indian mobile number — should it be +91 India? Press Cancel to change it, or OK to continue.`)) return;
    setSending(true); setError("");
    try {
      const waRaw = form.whatsapp_same ? "" : form.whatsapp.trim();
      const whatsapp = waRaw ? (waRaw.startsWith("+") ? waRaw : `${dialCode}${waRaw}`) : `${dialCode}${digits}`;
      await api.post("/guide-reg", { token, name: form.name.trim(), mobile: `${dialCode}${digits}`, whatsapp, email: form.email.trim() || null, profession: form.profession.trim(), place: form.place.trim() });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="guide-info-page" className="min-h-screen bg-[#EDECE3] text-foreground">
      <div className="mx-auto max-w-lg px-6 py-10">
        <Link to="/" data-testid="guide-info-back-home" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent"><ArrowLeft size={14} /> Home</Link>
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 rounded-3xl border border-border bg-background/90 backdrop-blur p-7 sm:p-9">
          <p className="text-xs uppercase tracking-[0.3em] text-accent">{meta ? `${meta.site_title}${meta.site_title2 ? ` · ${meta.site_title2}` : ""}` : "Positive Life @SSVS"}</p>
          {invalid ? (
            <div className="mt-5" data-testid="guide-info-invalid">
              <h1 className="font-serif text-3xl">Link not available</h1>
              <p className="mt-2 text-sm text-muted-foreground">{invalid}. Please ask the organiser for a fresh link.</p>
            </div>
          ) : done ? (
            <div className="mt-5" data-testid="guide-info-success">
              <CheckCircle2 size={36} className="text-accent" />
              <h1 className="font-serif text-3xl mt-3">Thank you, {form.name.trim()}.</h1>
              <p className="mt-2 text-sm text-muted-foreground">Your Guides Information has been recorded. The organiser will be in touch.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-5" data-testid="guide-info-form">
              <div>
                <h1 className="font-serif font-light text-3xl tracking-tight leading-[1.05] mb-2">Guides Information</h1>
                <p className="text-muted-foreground text-sm">Please fill in your details once. {meta?.batch_no ? `Batch ${meta.batch_no}.` : ""}</p>
              </div>
              <div><Label>Full name</Label><input required data-testid="guide-info-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} /></div>
              <div>
                <Label>Mobile number</Label>
                <div className="flex gap-2 mt-2">
                  <select data-testid="guide-info-country-code" value={dialCode} onChange={(e) => setDialCode(e.target.value)} className="bg-transparent border-b border-border py-2.5 outline-none text-sm">
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                  </select>
                  <input required type="tel" inputMode="numeric" data-testid="guide-info-mobile" value={mobileLocal} onChange={(e) => setMobileLocal(e.target.value.replace(/\D/g, ""))} placeholder="Mobile number" className="w-full bg-transparent border-b border-border py-2.5 outline-none focus:border-accent placeholder:text-muted-foreground/50" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>WhatsApp number</Label>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" data-testid="guide-info-whatsapp-same" checked={form.whatsapp_same} onChange={(e) => setForm({ ...form, whatsapp_same: e.target.checked })} className="w-3.5 h-3.5 accent-[#1A2E26]" /> Same as mobile</label>
                </div>
                <input type="tel" inputMode="numeric" data-testid="guide-info-whatsapp" disabled={form.whatsapp_same} value={form.whatsapp_same ? `${dialCode}${mobileLocal}` : form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/[^\d+]/g, "") })} placeholder="WhatsApp number with country code" className={`${inputCls} disabled:opacity-60`} />
              </div>
              <div><Label>Email</Label><input type="email" data-testid="guide-info-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} /></div>
              <div><Label>Profession</Label><input required data-testid="guide-info-profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="What you do" className={inputCls} /></div>
              <div><Label>Place</Label><input required data-testid="guide-info-place" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="City / Town" className={inputCls} /></div>
              {error && <p data-testid="guide-info-error" className="text-sm text-destructive">{error}</p>}
              <button data-testid="guide-info-submit" disabled={sending || !meta} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                {sending ? "Submitting…" : "Submit"} <ArrowRight size={14} />
              </button>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Heart size={11} className="text-accent" /> Your details are shared only with the Positive Life organisers.</p>
            </form>
          )}
        </motion.section>
      </div>
    </main>
  );
}
